import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { appURL } from "../../../config";
import { BsThreeDotsVertical } from "react-icons/bs";
import { HiUserAdd, HiLink } from "react-icons/hi";
import { HiUsers } from "react-icons/hi2";

import Loader from "../../../components/loader";
import api from "../../../services/api";
import Modal from "../../../components/modal";
import SelectUser from "../../../components/selectUser";
import { copyToClipboard } from "../../../utils";

export default function EditProject() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [project, setProject] = useState(null);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);



  const { id } = useParams();

  useEffect(() => {
    getUsers();
    getProject();
  }, []);

  const getUsers = async () => {
    try {
      setLoading(true);
      const {ok, data} = await api.post(`/user/search`, { project_ids: [id], status: "active" });
      if (!ok) throw new Error("Failed to get users");
      setUsers(data.users);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  const getProject = async () => {
    try {
      setLoading(true);
      const {ok, data} = await api.get(`/project/${id}`);
      if (!ok) throw new Error("Failed to get project");
      setProject(data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  if (loading)
    return (
      <div>
        <Loader />
      </div>
    );

  return (
    <div className="relative">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-3 hover:border-blue-500 transition-colors cursor-pointer group"
          onClick={() => setShowMenu(true)}>
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-50">
            <HiUserAdd className="w-8 h-8 text-gray-400 group-hover:text-blue-500" />
          </div>
          <div className="text-center">
            <h3 className="font-medium text-gray-900">Add team member</h3>
            <p className="text-sm text-gray-500">Click to see options</p>
          </div>
        </div>
        {users?.map((user) => (
          <UserCard key={user._id} user={user} project={project} callback={getUsers} />
        ))}
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <CreateUserForm
          project={project}
          onClose={() => setShowCreateModal(false)}
          onSubmit={async (userData) => {
            try {
              userData.role = "client";
              await api.post("/user/invite", userData);
              toast.success("User created and added to team");
              getUsers();
              setShowCreateModal(false);
            } catch (error) {
              if (error.code === "EMAIL_ALREADY_EXIST") toast.error("User already exists\nTry adding them directly to the team.");
              else toast.error("Failed to create user");
              console.error(error);
            }
          }}
        />
      </Modal>
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)}>
        <div className="p-6 space-y-6 max-w-sm mx-auto min-h-96">
          <div className="flex items-center gap-2 mb-1">
            <HiUsers className="w-5 h-5 text-green-500" />
            <h3 className="text-sm font-medium">Add existing user</h3>
          </div>
          <SelectUser
            userRoles={["admin", "user", "client"]}
            placeholder="Select user"
            className="w-full px-2 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
            onChange={async (user) => {
              if (!user.project_ids.includes(project._id)) {
                const project_ids = [...user.project_ids, project._id];
                await api.put(`/user/${user._id}`, { project_ids });
                toast.success("User assigned");
                getUsers();
              } else {
                toast.success("User is already assigned to this project");
              }
            }}
          />
        </div>
      </Modal>

      <Modal isOpen={showMenu} onClose={() => setShowMenu(false)}>
        <div className="p-6 space-y-6 max-w-sm mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Add team member</h2>
            <p className="mt-2 text-sm text-gray-600">Choose how you'd like to add a team member</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setShowCreateModal(true);
                setShowMenu(false);
              }}
              className="w-full p-4 text-left bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <HiUserAdd className="w-6 h-6 text-blue-500" />
              <div>
                <h3 className="font-medium text-gray-900">Add new team member</h3>
                <p className="text-sm text-gray-500">Create a new user account</p>
              </div>
            </button>

            <button
              onClick={() => {
                setShowInviteModal(true);
                setShowMenu(false);
              }}
              className="w-full p-4 text-left bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <HiUsers className="w-6 h-6 text-green-500" />
              <div>
                <h3 className="font-medium text-gray-900">Add existing user</h3>
                <p className="text-sm text-gray-500">Select from existing users</p>
              </div>
            </button>

            <button
              onClick={() => {
                copyToClipboard(`${appURL}/auth/invite?redirect=${project._id}&application=accounting`);
                toast.success("Link copied!");
                setShowMenu(false);
              }}
              className="w-full p-4 text-left bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-3">
              <HiLink className="w-6 h-6 text-purple-500" />
              <div>
                <h3 className="font-medium text-gray-900">Share invite link</h3>
                <p className="text-sm text-gray-500">Copy invitation link</p>
              </div>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const UserCard = ({ user, project, callback }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const [showMenu, setShowMenu] = useState(false);

  const removeMember = async () => {
    try {
      if (!confirm(`Are you sure you want to remove ${user.name} from the team?`)) return;
      const project_ids = user.project_ids.filter((id) => id !== project._id);
      const { ok } = await api.put(`/user/${user._id}`, { project_ids });
      if (!ok) throw new Error("Failed to remove user");
      toast.success("User removed");
      callback();
    } catch (e) {
      toast.error("Failed to remove user");
      console.log(e);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={user?.avatar} alt={user?.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-offset-2 ring-gray-100" />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 border-2 border-white bg-green-400 rounded-full"></div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{user?.name}</h3>
            <p className="text-sm text-gray-500">{user?.jobTitle}</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu((p) => !p);
            }}
            className="p-1.5 bg-white rounded-full border border-gray-300 hover:bg-gray-100">
            <BsThreeDotsVertical className="w-4 h-4 text-gray-600" />
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    query.set("user_modal_id", user._id);
                    query.set("user_modal_tab", "skills");
                    navigate({ search: query.toString() });
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left">
                  View Profile
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMember();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 text-left">
                  Remove from Team
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="text-sm text-gray-600">
        <p className="truncate">{user?.email}</p>
        <p className="truncate">{user?.last_login_at ? new Date(user?.last_login_at).toLocaleString() : "Never logged in"}</p>
      </div>
    </div>
  );
};

const CreateUserForm = ({ project, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({ email: "", project_ids: [project._id], application: "accounting" });

  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      onSubmit(formData);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <HiUserAdd className="w-5 h-5 text-blue-500" />
        <h3 className="text-sm font-medium">Add new team member</h3>
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            type="email"
            required
            placeholder="john@example.com"
            className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2.5 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          Cancel
        </button>
        <button
          disabled={loading}
          type="submit"
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Invite user
        </button>
      </div>
    </form>
  );
};
